// "12 days from last visit" / "Never visited" (iOS `daysSinceText`) in the days-since-visit color of the iOS map:
// ≤ 7 days green, ≤ 30 amber, older red, never gray (WEB_PLAN.md §1.1). The long form carries the walking-figure badge
// of RecordRowVisitLine; the short form ("12 days") is for table cells and compact cards.

import { Footprints } from 'lucide-react';
import { IconBadge, cn, readableForeground } from '@/ui';
import { visitRecency, type VisitRecency } from './storeRowModel';

export interface DaysSinceVisitProps {
  /** The derived last visit (`statsOf(stats, id).lastVisitAt`); undefined = never visited. */
  lastVisitAt?: Date | null | undefined;
  /** A precomputed `visitRecency(...)` (StoreRow's model); wins over `lastVisitAt`/`now`. */
  recency?: VisitRecency;
  /** Clock for the day count (defaults to now; pass the page's `now` so a list agrees with itself). */
  now?: Date;
  /** `long`: "12 days from last visit" with the badge; `short`: a colored dot and "12 days". */
  format?: 'long' | 'short';
  className?: string;
}

export function DaysSinceVisit({ lastVisitAt, recency, now, format = 'long', className }: DaysSinceVisitProps) {
  const v = recency ?? visitRecency(lastVisitAt, now ?? new Date());
  const ink = readableForeground(v.color);
  if (format === 'short') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums', className)} title={v.text}>
        <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ backgroundColor: v.color }} />
        <span className={v.bucket === 'never' ? 'text-fg-muted' : 'text-fg'}>
          <span className="sr-only">{v.text}</span>
          <span aria-hidden>{v.shortText}</span>
        </span>
      </span>
    );
  }
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <IconBadge icon={<Footprints strokeWidth={2.4} />} color={v.color} size={24} />
      <span className="truncate text-xs font-medium" style={{ color: ink }}>
        {v.text}
      </span>
    </span>
  );
}
